import { createContext, useContext, useEffect, useState } from "react";
import { AuthContext } from "../Authorisation/AuthContext";
import { APIContext } from "./APIContext";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FormContainer, useForm } from "react-hook-form-mui";
import ValidatedTextField from "../Utilities/Forms/ValidatedTextField";
import { TERMS_AND_CONDITIONS_SETTING_NAME, TERMS_AND_CONDITIONS_VERSION } from "../Authorisation/TermsAndConditions";

type ContextModel = {
    openMailchimpPrompt: () => void;
};

class LoginPromptStateViewModel {
    public officeConfirmationDue: boolean = false;
    public officeChangePending: boolean = false;
    public mailchimpDue: boolean = false;
    public organisationName: string = '';
    public officeName: string = '';
    public displayedConfirmationText: string = '';
    public pendingReportedOrganisationName: string | null = null;
    public pendingReportedOfficeName: string | null = null;
    public pendingAdditionalDetails: string | null = null;
}

class OfficeUpdateRequestViewModel {
    public reportedOrganisationName: string = '';
    public reportedOfficeName: string = '';
    public additionalDetails: string = '';
}

export const UserPromptContext = createContext<ContextModel>({
    openMailchimpPrompt: () => { throw Error('Cannot use a context without a provider'); }
});

type DialogState = 'officeConfirmation' | 'officePending' | 'officeUpdate' | 'mailchimp' | null;

export default function UserPromptContextProvider({ children }: React.PropsWithChildren<{}>) {
    const { authenticated, userDetails, refreshUserDetails } = useContext(AuthContext);
    const { get, post, getUserSetting, showMessage } = useContext(APIContext);
    const [dialogState, setDialogState] = useState<DialogState>(null);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [promptState, setPromptState] = useState<LoginPromptStateViewModel>(new LoginPromptStateViewModel());
    const [officeHandledThisSession, setOfficeHandledThisSession] = useState(false);
    const [officeConfirmationChecked, setOfficeConfirmationChecked] = useState(false);
    const [mailchimpDueThisSession, setMailchimpDueThisSession] = useState(false);
    const [mailchimpHandledThisSession, setMailchimpHandledThisSession] = useState(false);
    const [manualMailchimpOpen, setManualMailchimpOpen] = useState(false);
    const [mailchimpChecked, setMailchimpChecked] = useState(true);
    const [officeDialogSource, setOfficeDialogSource] = useState<'officeConfirmation' | 'officePending'>('officeConfirmation');

    const officeUpdateForm = useForm<OfficeUpdateRequestViewModel>({
        defaultValues: new OfficeUpdateRequestViewModel()
    });

    const loadPromptState = () => {
        get('UserPrompt', 'GetLoginPromptState', (data) => {
            const nextState = data as LoginPromptStateViewModel;
            setPromptState(nextState);
            setOfficeHandledThisSession(false);
            setOfficeConfirmationChecked(false);
            setMailchimpDueThisSession(nextState.mailchimpDue);
            setMailchimpHandledThisSession(false);
            setMailchimpChecked(true);
        });
    };

    useEffect(() => {
        if (!authenticated) {
            setTermsAccepted(false);
            setDialogState(null);
            return;
        }

        getUserSetting(TERMS_AND_CONDITIONS_SETTING_NAME, (value) => {
            const accepted = value === TERMS_AND_CONDITIONS_VERSION;
            setTermsAccepted(accepted);
            if (accepted) {
                loadPromptState();
            }
        });
    }, [authenticated]);

    useEffect(() => {
        const handleTermsAccepted = () => {
            setTermsAccepted(true);
            loadPromptState();
        };

        window.addEventListener('terms-accepted', handleTermsAccepted);
        return () => window.removeEventListener('terms-accepted', handleTermsAccepted);
    }, []);

    useEffect(() => {
        if (!authenticated || !termsAccepted || dialogState !== null || manualMailchimpOpen) {
            return;
        }

        if (!officeHandledThisSession) {
            if (promptState.officeChangePending) {
                setDialogState('officePending');
                return;
            }

            if (promptState.officeConfirmationDue) {
                setDialogState('officeConfirmation');
                return;
            }
        }

        if (!mailchimpHandledThisSession && mailchimpDueThisSession && !userDetails?.mailchimpOptedIn) {
            setDialogState('mailchimp');
        }
    }, [authenticated, termsAccepted, dialogState, manualMailchimpOpen, officeHandledThisSession, promptState, mailchimpHandledThisSession, mailchimpDueThisSession, userDetails?.mailchimpOptedIn]);

    const closeMailchimp = async (submitOptIn: boolean) => {
        if (submitOptIn && mailchimpChecked) {
            await post('UserPrompt', 'SubmitMailchimpOptIn', {}, async () => {
                await refreshUserDetails();
            });
        }

        setMailchimpHandledThisSession(true);
        setManualMailchimpOpen(false);
        setDialogState(null);
        setMailchimpChecked(true);
    };

    const confirmOfficeAssignment = async () => {
        await post('UserPrompt', 'ConfirmOfficeAssignment', {}, async () => {
            await refreshUserDetails();
            setOfficeHandledThisSession(true);
            setOfficeConfirmationChecked(false);
            setDialogState(null);
        });
    };

    const openOfficeUpdateDialog = (source: 'officeConfirmation' | 'officePending') => {
        setOfficeDialogSource(source);
        officeUpdateForm.reset({
            reportedOrganisationName: source === 'officeConfirmation'
                ? promptState.organisationName
                : (promptState.pendingReportedOrganisationName ?? ''),
            reportedOfficeName: source === 'officeConfirmation'
                ? promptState.officeName
                : (promptState.pendingReportedOfficeName ?? ''),
            additionalDetails: source === 'officeConfirmation'
                ? ''
                : (promptState.pendingAdditionalDetails ?? '')
        });
        setDialogState('officeUpdate');
    };

    const submitOfficeUpdateRequest = async (form: OfficeUpdateRequestViewModel) => {
        await post('UserPrompt', 'ReportOfficeAssignment', form, async () => {
            await refreshUserDetails();
            setOfficeHandledThisSession(true);
            setDialogState(null);
            showMessage('success', 'Your office update request has been submitted.');
        });
    };

    const closeOfficeUpdateDialog = () => {
        setDialogState(officeDialogSource);
    };

    const handlePendingContinue = () => {
        setOfficeHandledThisSession(true);
        setDialogState(null);
    };

    const openMailchimpPrompt = () => {
        if (userDetails?.mailchimpOptedIn) {
            return;
        }

        setManualMailchimpOpen(true);
        setMailchimpChecked(true);
        setDialogState('mailchimp');
    };

    return (
        <UserPromptContext.Provider value={{ openMailchimpPrompt }}>
            {children}

            <Dialog open={dialogState === 'officeConfirmation'} onClose={() => { }} disableEscapeKeyDown fullWidth maxWidth="sm">
                <DialogTitle>Annual Office Confirmation</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="body2">
                                Your user account, {userDetails?.userFullName}, is assigned to {promptState.organisationName}'s {promptState.officeName} office licence. By ticking the box below, you confirm you are based at this office. If you are not based at this office, please click Contact Us and we can reassign your account to the correct office licence.
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                control={<Checkbox checked={officeConfirmationChecked} onChange={(event) => setOfficeConfirmationChecked(event.target.checked)} />}
                                label={promptState.displayedConfirmationText}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => openOfficeUpdateDialog('officeConfirmation')}>Contact Us</Button>
                    <Button onClick={confirmOfficeAssignment} variant="contained" disabled={!officeConfirmationChecked}>Continue</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogState === 'officePending'} onClose={handlePendingContinue} fullWidth maxWidth="sm">
                <DialogTitle>Office Change Pending</DialogTitle>
                <DialogContent>
                    <Typography>You have declared your licence is incorrectly assigned. You can contact us here to give additional information.</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => openOfficeUpdateDialog('officePending')}>Contact Us</Button>
                    <Button onClick={handlePendingContinue} variant="contained">Continue</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dialogState === 'officeUpdate'} onClose={closeOfficeUpdateDialog} fullWidth maxWidth="sm">
                <DialogTitle>Office Update Request</DialogTitle>
                <DialogContent>
                    <FormContainer formContext={officeUpdateForm} onSuccess={submitOfficeUpdateRequest}>
                        <Grid container spacing={2} paddingTop="8px">
                            <Grid size={12}>
                                <Typography variant="body2">Current organisation: {promptState.organisationName}</Typography>
                                <Typography variant="body2">Current office: {promptState.officeName}</Typography>
                            </Grid>
                            <Grid size={12}>
                                <ValidatedTextField name="reportedOrganisationName" label="Organisation" required />
                            </Grid>
                            <Grid size={12}>
                                <ValidatedTextField name="reportedOfficeName" label="Office" required />
                            </Grid>
                            <Grid size={12}>
                                <ValidatedTextField name="additionalDetails" label="Additional Details" multiline rows={4} />
                            </Grid>
                            <Grid size={12}>
                                <DialogActions sx={{ paddingX: 0 }}>
                                    <Button onClick={closeOfficeUpdateDialog}>Cancel</Button>
                                    <Button type="submit" variant="contained">Submit</Button>
                                </DialogActions>
                            </Grid>
                        </Grid>
                    </FormContainer>
                </DialogContent>
            </Dialog>

            <Dialog
                open={dialogState === 'mailchimp'}
                onClose={() => closeMailchimp(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Receive important trics updates</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid size={12}>
                            <Typography variant="body2">Make sure you receive important TRICS project updates through our Mailchimp service</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body2">By receiving these emails, you will increase your knowledge of TRICS and the work that we are undertaking across all areas of our project.</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography variant="body2">It is important for us here at TRICS to provide as much helpful information as possible to the TRICS Community. Therefore, our correspondence includes the following:</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography component="div" variant="body2">
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    <li>TRICS system updates and new surveys being added to the database.</li>
                                    <li>Significant new system features and work in progress.</li>
                                    <li>The ongoing integration with TRICS and Vision-Led Transport Planning.</li>
                                    <li>Important updates on TRICS licensing arrangements.</li>
                                    <li>Information on our annual data collection programmes and SAM surveys.</li>
                                    <li>Updates on the TRICS Good Practice Guide and other key documentation.</li>
                                    <li>Research projects and technical analyses, and the availability of reports.</li>
                                    <li>Information on free User Events (the User Meeting and the Training & Development Forum).</li>
                                    <li>The Annual TRICS User Survey to have your say on the system’s future.</li>
                                    <li>Links to articles written by the TRICS team.</li>
                                    <li>Any other important updates for the TRICS Community.</li>
                                </ul>
                            </Typography>
                        </Grid>
                        <Grid size={12}>
                            <FormControlLabel
                                control={<Checkbox checked={mailchimpChecked} onChange={(event) => setMailchimpChecked(event.target.checked)} />}
                                label="I would like to remain fully informed about the TRICS project and receive these important updates through our Mailchimp service."
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => closeMailchimp(true)}>Continue</Button>
                </DialogActions>
            </Dialog>
        </UserPromptContext.Provider>
    );
}
